import React, { useMemo, useCallback } from "react";
import PageLoader from "components/ui/PageLoader";
import "assets/css/style.css";
import { useGetCompanySubscriptionsAdmin } from "apis/companySubscription";
import { formatDate } from "utils/helperFunctions";
import Table from "components/ui/Table";

function escapeCsvValue(value) {
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function downloadCsv(headers, rows, filename) {
  const csvContent = [
    headers.map(escapeCsvValue).join(","),
    ...rows.map((row) => row.map(escapeCsvValue).join(",")),
  ].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const CompanySubscriptions = () => {
  const pageSize = 10;

  const { data: subscriptionsData, isPending } =
    useGetCompanySubscriptionsAdmin();

  const data = subscriptionsData?.data ?? [];

  const handleExport = useCallback(() => {
    if (!data || data.length === 0) return;

    const headers = [
      "#",
      "Company",
      "Status",
      "Seats Qty",
      "Seat Price",
      "Main Agents",
      "Addons",
      "Monthly Total",
      "Period Start",
      "Period End",
      "Billing Email",
    ];

    const csvRows = data.map((row, index) => {
      const agents = (row.mainAgents || [])
        .map((a) => a.userId?.name || "Unknown")
        .join("; ");
      const addons = (row.addons || [])
        .filter((a) => a.enabled)
        .map((a) => `${a.name} ($${a.unitPrice.toFixed(2)})`)
        .join("; ");

      return [
        index + 1,
        row.companyId?.name || "N/A",
        row.status || "",
        row.seats?.quantity || 0,
        row.seats?.unitPrice ? row.seats.unitPrice.toFixed(2) : "0.00",
        agents || "No agents",
        addons || "None",
        row.totalMonthlyAmount ? row.totalMonthlyAmount.toFixed(2) : "0.00",
        row.currentPeriodStart ? formatDate(row.currentPeriodStart) : "N/A",
        row.currentPeriodEnd ? formatDate(row.currentPeriodEnd) : "N/A",
        row.billingEmail || "",
      ];
    });

    downloadCsv(headers, csvRows, "company-subscriptions");
  }, [data]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "companyId.name",
        header: "Company",
        cell: (row) => <span>{row.companyId?.name || "N/A"}</span>,
        isFilter: true,
        minWidth: "150px",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => {
          const status = row.status;
          let statusClass = "";
          switch (status) {
            case "active":
              statusClass = "bg-green-100 text-green-800";
              break;
            case "trialing":
              statusClass = "bg-yellow-100 text-yellow-800";
              break;
            case "canceled":
              statusClass = "bg-red-100 text-red-800";
              break;
            case "past_due":
              statusClass = "bg-orange-100 text-orange-800";
              break;
            case "unpaid":
              statusClass = "bg-gray-100 text-gray-800";
              break;
            case "paused":
              statusClass = "bg-blue-100 text-blue-800";
              break;
            default:
              statusClass = "bg-gray-100 text-gray-800";
          }
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${statusClass}`}
            >
              {status}
            </span>
          );
        },
        isFilter: true,
      },
      {
        accessorKey: "seats",
        header: "Seats",
        cell: (row) => {
          const seats = row.seats;
          return (
            <span>
              {seats?.quantity || 0} × $
              {seats?.unitPrice ? seats.unitPrice.toFixed(2) : "0.00"}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "mainAgents",
        header: "Main Agents",
        cell: (row) => {
          const agents = row.mainAgents || [];
          if (agents.length === 0) {
            return <span className="text-darkgray text-sm">No agents</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {agents.map((agent, index) => (
                <span
                  key={agent.userId?._id || index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                  title={agent.userId?.email || agent.userId?.name}
                >
                  {agent.userId?.name || "Unknown"}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
        minWidth: "200px",
      },
      {
        accessorKey: "addons",
        header: "Addons",
        cell: (row) => {
          const addons = (row.addons || []).filter((a) => a.enabled);
          if (addons.length === 0) {
            return <span className="text-darkgray text-sm">None</span>;
          }
          return (
            <div className="flex flex-wrap gap-1">
              {addons.map((addon, index) => (
                <span
                  key={addon.key || index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                  title={`${addon.billingType} - $${addon.unitPrice.toFixed(2)}`}
                >
                  {addon.name}
                </span>
              ))}
            </div>
          );
        },
        isFilter: false,
        minWidth: "200px",
      },
      {
        accessorKey: "totalMonthlyAmount",
        header: "Monthly Total",
        cell: (row) => (
          <span className="font-medium">
            $
            {row.totalMonthlyAmount
              ? row.totalMonthlyAmount.toFixed(2)
              : "0.00"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "currentPeriodStart",
        header: "Period Start",
        cell: (row) => (
          <span>
            {row.currentPeriodStart
              ? formatDate(row.currentPeriodStart)
              : "N/A"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "currentPeriodEnd",
        header: "Period End",
        cell: (row) => (
          <span>
            {row.currentPeriodEnd ? formatDate(row.currentPeriodEnd) : "N/A"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "billingEmail",
        header: "Billing Email",
        cell: (row) => (
          <span className="text-sm">{row.billingEmail || "—"}</span>
        ),
        isFilter: true,
      },
    ],
    []
  );

  if (isPending) {
    return <PageLoader />;
  }

  return (
    <div className="container mx-auto">
      <Table
        title=""
        columns={columns}
        data={data || []}
        pageSize={pageSize}
        ButtonCmp={() => (
          <button onClick={handleExport} className="black-button">
            Export
          </button>
        )}
      />
    </div>
  );
};

export default CompanySubscriptions;
